export class FileReadError extends Error {
  constructor(fileName, cause) {
    super(`Failed to read file: ${fileName}`)
    this.name = 'FileReadError'
    this.fileName = fileName
    this.cause = cause
  }
}

// 読み込み自体は成功したが中身が 0 バイトだった場合。「読み込めなかった」とは
// 原因も対処も違う（保存できない空ファイル）ため、FileReadError とは別扱いにする。
export class EmptyFileError extends Error {
  constructor(fileName) {
    super(`File is empty: ${fileName}`)
    this.name = 'EmptyFileError'
    this.fileName = fileName
  }
}

// FileReader の onloadend は成功・失敗の両方で発火し、失敗時 reader.result は null になる。
// onloadend で resolve すると失敗を検知できず null がそのままAPIへ送られてしまうため、
// 成功(onload)と失敗(onerror/onabort)を明確に分けて扱う。
const readAs = (blob, method, fileName) => {
  return new Promise((resolve, reject) => {
    const name = fileName ?? blob?.name ?? ''
    const reader = new FileReader()
    reader.onload = () => {
      // null / undefined / 空文字はいずれも「読めなかった」として扱い、
      // 呼び出し側がAPIへ送ってしまわないよう必ず reject する。
      if (!reader.result) {
        reject(new FileReadError(name, reader.error))
        return
      }
      resolve(reader.result)
    }
    reader.onerror = () => reject(new FileReadError(name, reader.error))
    reader.onabort = () => reject(new FileReadError(name, reader.error))
    try {
      reader[method](blob)
    } catch (e) {
      // フォルダをドロップした場合など、読み込み開始時点で例外になるケース。
      reject(new FileReadError(name, e))
    }
  })
}

// "data:<mime>;base64,<data>" 形式で読み込む。
export const readFile = (blob, fileName) => readAs(blob, 'readAsDataURL', fileName)

// ファイルの中身をメモリ上のBlobとして確保する。
// Outlookの添付を直接ドラッグした場合、実体はファイルではなく仮想ファイルであり、
// Chromeが一時フォルダへ書き出したものを File として渡してくる。この一時ファイルは
// 数秒で削除されることがあり、保存時に読もうとすると NotFoundError になる
// （File はサイズ・更新日時のスナップショットなので、実体が消えると読めない）。
// 追加した時点で読み切ってしまうことで、この時間差をなくす。
export const snapshotFile = async (file) => {
  const buffer = await readAs(file, 'readAsArrayBuffer')
  // 読み込みは成功したが 0 バイトだった場合（元から空のファイル、または仮想ファイルが
  // 実体化されなかった場合）。空ファイルは保存できないため、読み込み失敗とは分けて弾く。
  if (buffer.byteLength === 0) {
    throw new EmptyFileError(file?.name ?? '')
  }
  return new Blob([buffer], { type: file.type || 'application/octet-stream' })
}
